import { useCallback, useMemo } from 'react';
import mapboxgl from 'mapbox-gl';

interface MapAnimationConfig {
  initialLoad: {
    delay: number;
    duration: number;
    curve: number;
    easing: (t: number) => number;
    zoom: number;
    pitch: number;
  };
  transition: {
    delay: number;
    duration: number;
    curve: number;
    easing: (t: number) => number;
    zoom: number;
    pitch: number;
  };
  globe: {
    center: [number, number];
    zoom: number;
  };
}

// Create stable easing function outside component
const smoothstep = (t: number) => t * t * (3 - 2 * t);

export function useMapAnimations() {
  const config = useMemo<MapAnimationConfig>(() => ({
    initialLoad: {
      delay: 500,
      duration: 2500,
      curve: 1.2,
      easing: smoothstep,
      zoom: 16,
      pitch: 45,
    },
    transition: {
      delay: 100,
      duration: 1500,
      curve: 1.2,
      easing: smoothstep,
      zoom: 16,
      pitch: 45,
    },
    globe: {
      center: [0, 20] as [number, number],
      zoom: 0.8,
    },
  }), []);

  const flyToLocation = useCallback(
    (
      map: mapboxgl.Map,
      longitude: number,
      latitude: number,
      type: 'initialLoad' | 'transition' = 'transition'
    ) => {
      const animConfig = config[type];

      setTimeout(() => {
        map.flyTo({
          center: [longitude, latitude],
          zoom: animConfig.zoom,
          duration: animConfig.duration,
          curve: animConfig.curve,
          easing: animConfig.easing,
          essential: true,
        });
      }, animConfig.delay);
    },
    [config]
  );

  return {
    config,
    flyToLocation,
  };
}
